import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import type { ChangeCatalogItem } from '../../../types/changes';
import SearchableSelect from './SearchableSelect';

interface Props {
  id: string;
  label: string;
  items: ChangeCatalogItem[];
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  searchEnabled?: boolean;
  /** Cadastro atual da mudança — reaparece mesmo se foi desativado depois. */
  current?: { id: number; name: string } | null;
}

/**
 * Select de um cadastro de apoio (categoria, área solicitante).
 *
 * O select do formulário só recebe cadastros ativos. Quando a mudança aponta
 * para um item que foi desativado depois, ele é reinserido na lista para não
 * sumir da tela de edição — sem isso o campo apareceria vazio e um salvamento
 * de outro campo trocaria a categoria silenciosamente.
 */
const CatalogSelect: React.FC<Props> = ({
  id,
  label,
  items,
  value,
  onChange,
  disabled,
  searchEnabled = false,
  current,
}) => {
  const options =
    current && current.id > 0 && !items.some((item) => item.id === current.id)
      ? [{ id: current.id, name: current.name, is_active: false, description: null }, ...items]
      : items;
  const isMissing = value > 0 && !options.some((item) => item.id === value);

  const selectOptions = useMemo(() => {
    const rows = [
      ...(isMissing ? [{ value: value, label: 'Cadastro desativado (atual)' }] : []),
      ...options.map((item) => ({
        value: item.id,
        label: item.is_active === false ? `${item.name} (desativado)` : item.name,
      })),
    ];
    return rows;
  }, [isMissing, options, value]);

  return (
    <>
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <SearchableSelect
        id={id}
        ariaLabel={label}
        value={value || ''}
        disabled={disabled}
        searchEnabled={searchEnabled}
        options={selectOptions}
        onChange={(next) => onChange(Number(next) || 0)}
      />
      {items.length === 0 ? (
        <Form.Text className="text-muted">
          Nenhum cadastro ativo. Cadastre em Configurações.
        </Form.Text>
      ) : null}
    </>
  );
};

export default CatalogSelect;
